import CountdownBar from './CountdownBar';
import FreezeButton from './FreezeButton';
import './StagePhaseIndicator.css';

// Top-center stage/phase indicator: seven boxes (one per stage the engine actually broadcasts,
// EXCLUDING "Deal" and "Determine Winner") laid out left-to-right in play order. Sizing is
// anchored to the table-info box's BASELINE size (measured in GameTable off the hidden
// `.game-info-baseline` twin and handed in as infoBaseSize): every box is as WIDE as that box,
// and 30% of its HEIGHT clamped to `maxBoxHeight` — the room that actually exists above the
// top-center seat's Power-Up tier pips.
//
// The baseline is deliberately NOT the info box's live size. That box grows and shrinks all
// round as situational rows come and go (the pot, the stage line, a bomb-pot banner, an
// SnG/tournament level), and a wide row like the bomb-pot banner would otherwise stretch all
// seven boxes across the screen mid-hand. Anchored to the baseline, this panel is the same size
// the whole session — the size it has the moment a player sits down.
//
// Dynamic highlight when a box's stage is the current stage: yellow border + bold name; the
// matching phase button lights yellow (phaseless stages just light their single button while
// current); and the betting-street boxes show the live phase name at the bottom. Everything
// returns to default the moment the current stage no longer matches.
//
// Mostly a status readout — the container stays pointer-events: none — but freeze time adds
// two pointer-events islands: the dots become clickable QUEUE buttons for seated players
// (pick the stage/phase point where a one-per-round 10s freeze fires; light-blue hover,
// dark-blue while selected, only the OWNER sees their private selection), and the circular
// Freeze/Unfreeze button (FreezeButton) docks just right of the box row.

// Phase buttons on the betting-street boxes, left-to-right = start -> decision -> end (the
// server's currentPhase progression for those stages).
const PHASES = ['start', 'decision', 'end'];

// phased: the box carries three phase buttons + a live phase label. A phaseless box carries a
// single centered button. All boxes are the same width.
const STAGE_BOXES = [
  { key: 'exchange', label: 'Exchange', phased: false },
  { key: 'predeal',  label: 'Pre-Deal', phased: false },
  { key: 'preflop',  label: 'Pre-Flop', phased: true },
  { key: 'flop',     label: 'Flop',     phased: true },
  { key: 'turn',     label: 'Turn',     phased: true },
  { key: 'river',    label: 'River',    phased: true },
  { key: 'showdown', label: 'Showdown', phased: false },
];

const StagePhaseIndicator = ({ currentStage, currentPhase, infoBaseSize, pupActive = false, phaseDurationMs = 2000, phaseStartTime = null,
                              // Live px room above the top-center seat's Power-Up tier pips
                              // (useTableGeometry.topPipCeilingPx); null until measured.
                              maxBoxHeight = null,
                              // Freeze time: queued-freeze dot picks + the Freeze/Unfreeze button island.
                              freezeActive = false, queueEnabled = false, queuedSelection = null, onQueueSelect = null,
                              roomId = null, mySeat = null, frozenSeats = [], freezeTimeBank = 0,
                              freezeStartTime = null, freezeDurationMs = null,
                              // First-time discoverability hint (GameTable arms it at Turn-stage entry
                              // for accounts that have never clicked a dot): while true, the clickable
                              // dots blink yellow for 2s (.spi-btn-hint; CSS keeps gold/queued dots as-is).
                              hintFlash = false }) => {
  // Nothing to size against until the info box's baseline twin has been measured.
  if (!infoBaseSize || !infoBaseSize.width || !infoBaseSize.height) return null;

  // 30% of the info box's baseline height, CLAMPED so the row can never cover the top-center
  // seat's Power-Up tier pips. The ceiling is measured from the live table, so it self-corrects on
  // resize and in split-screen instead of being a fraction tuned to one window; null
  // (pre-measurement, or jsdom) means "don't clamp". The 12px guard is only for a degenerate
  // window — it is well below the ~32px at which .spi-name (15px) + .spi-phase (11px) + the dot
  // row stop fitting, which is the point where the boxes start looking cramped.
  const boxHeight = Math.max(12, Math.min(
    infoBaseSize.height * 0.3,
    Number.isFinite(maxBoxHeight) && maxBoxHeight > 0 ? maxBoxHeight : Infinity,
  ));
  const boxWidth = infoBaseSize.width;          // every box = the info box's baseline width
  const buttonSize = Math.max(6, boxHeight * 0.2); // small circular button, scaled to the box

  // The phase countdown, DOCKED to the bottom of the boxes and spanning their full width (it
  // used to float free in the gap above the felt). Two mutually exclusive sources, both
  // suppressed while a Power-Up freezes the beat (the on-felt overlay shows its own window)
  // AND while a player has time frozen (the clocks are stopped server-side — a draining bar
  // would lie; the resume's fresh phaseStartTime remounts it full):
  //   - a live betting street's Start/End beat (mount-anchored; remounts per beat via the key);
  //   - the interactive Pre-Deal window (server-clock-anchored via phaseStartTime).
  const roundActive = ['preflop', 'flop', 'turn', 'river'].includes(currentStage);
  const showBeat = roundActive && !pupActive && !freezeActive && (currentPhase === 'start' || currentPhase === 'end');
  const showPredeal = !pupActive && !freezeActive && currentStage === 'predeal' && !!phaseStartTime;
  // The dots are queue PICKS for seated players: a click posts /games/freezequeue for the
  // dot's point ("stage:phase" on betting streets, bare "stage" otherwise; clicking the
  // selected point toggles it off server-side).
  const clickable = queueEnabled && !!onQueueSelect;

  return (
    <div className="stage-phase-indicator">
      <div className="spi-row" style={{ height: `${boxHeight}px` }}>
        {STAGE_BOXES.map((box) => {
          const active = box.key === currentStage;
          return (
            <div
              key={box.key}
              className={`spi-box${active ? ' spi-box-active' : ''}`}
              style={{ width: `${boxWidth}px`, height: `${boxHeight}px` }}
            >
              {/* Stage name, top-center. Bold only while this stage is current. */}
              <div className="spi-name">{box.label}</div>

              {/* Buttons at the box's vertical midpoint: three (one per phase) on betting
                  streets, one centered on phaseless stages. */}
              <div
                className={`spi-buttons${box.phased ? '' : ' spi-buttons-single'}`}
                style={{ '--spi-btn': `${buttonSize}px` }}
              >
                {(box.phased ? PHASES : ['only']).map((ph) => {
                  // A phase button lights when its stage AND phase are current; a phaseless
                  // stage's single button lights whenever that stage is current. The gold
                  // current-state highlight WINS over the dark-blue queued highlight.
                  const on = box.phased ? (active && currentPhase === ph) : active;
                  const point = box.phased ? `${box.key}:${ph}` : box.key;
                  const queued = queueEnabled && queuedSelection === point;
                  return (
                    <button
                      key={ph}
                      type="button"
                      className={`spi-btn${clickable ? ' spi-btn-clickable' : ''}${queued ? ' spi-btn-queued' : ''}${on ? ' spi-btn-on' : ''}${clickable && hintFlash ? ' spi-btn-hint' : ''}`}
                      disabled={!clickable}
                      onClick={clickable ? () => onQueueSelect(box.key, box.phased ? ph : null) : undefined}
                      title={clickable
                        ? `Queue a 10s freeze at ${box.label}${box.phased ? ` (${ph})` : ''}`
                        : undefined}
                    />
                  );
                })}
              </div>

              {/* Live phase name, bottom-center — only on the current betting-street box. */}
              {box.phased && active && currentPhase && (
                <div className="spi-phase">{currentPhase}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Circular Freeze/Unfreeze button, absolutely positioned just right of the box row
          (a pointer-events island; hidden for spectators inside FreezeButton itself). */}
      <FreezeButton
        roomId={roomId}
        stage={currentStage}
        mySeat={mySeat}
        frozenSeats={frozenSeats}
        freezeTimeBank={freezeTimeBank}
        freezeStartTime={freezeStartTime}
        freezeDurationMs={freezeDurationMs}
        size={boxHeight}
      />

      {/* Countdown bar: top edge flush to the boxes' bottom, spanning all seven. */}
      {showBeat && (
        <CountdownBar
          key={`${currentPhase}-${phaseStartTime || 0}`}
          className="spi-countdown"
          durationMs={phaseDurationMs || 2000}
        />
      )}
      {showPredeal && (
        <CountdownBar
          key={`predeal-${phaseStartTime}`}
          className="spi-countdown"
          durationMs={phaseDurationMs || 4000}
          startTime={phaseStartTime}
        />
      )}
    </div>
  );
};

export default StagePhaseIndicator;
